import React from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CallIcon from '@mui/icons-material/Call'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import {Box} from '@mui/material'
import {
  getProductLineColors,
  PGButton,
  PGTypography,
  type ProductLineColors,
  useProductLine,
} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import {ReactComponent as ConsultationCalendarIcon} from '@progyny/assets/icons/consultation-calendar.svg'
import {ConsultationCard} from '@progyny/pages/Consultations/components/ConsultationCard'
import {RouteEnum, useRoutes} from '@progyny/routes'

export interface NoConsultationViewProps {
  isOnboarding?: boolean
  onBookClick?: () => void
}

const useStyles = makeStyles<{colors?: ProductLineColors}>()(
  (theme, {colors}) => ({
    card: {
      border: '1px solid #ededed',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '40px',
    },
    textBlock: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '20px',
    },
    iconBadge: {
      width: '88px',
      height: '88px',
      borderRadius: '999px',
      background: `${colors?.primaryLight || theme.palette.primary.light}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    subtitle: {
      color: '#6b7280',
      textAlign: 'center',
      maxWidth: '400px',
    },
    meta: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '7px',
      color: '#6b7280',
    },
    metaDivider: {
      width: '1px',
      height: '13px',
      background: `${colors?.primaryLight || theme.palette.primary.light}`,
    },
  })
)

const NoConsultationView: React.FC<NoConsultationViewProps> = ({
  isOnboarding = false,
  onBookClick,
}) => {
  const colors = getProductLineColors(useProductLine().productLine)
  const {classes} = useStyles({colors})
  const history = useHistory()
  const {getPath} = useRoutes()

  const {t} = useTranslation()

  const content = {
    fertility: {
      onboarding: {
        title: t('book_onboarding_call'),
        subtitle: t('consultations_no_consultation_view_onboarding_subtitle'),
      },
      upcoming: {
        title: t('consultations_no_consultation_view_fertility_title'),
        subtitle: t('consultations_no_consultation_view_subtitle'),
      },
    },
  }

  const productContent = content.fertility

  return (
    <ConsultationCard padding='lg' borderRadius='lg' className={classes.card}>
      <Box className={classes.content}>
        <Box className={classes.textBlock}>
          <Box className={classes.iconBadge}>
            <ConsultationCalendarIcon
              width={46}
              height={46}
              color={colors?.primary}
              fill={colors?.secondary}
            />
          </Box>
          <PGTypography variant='header' as='h2' textAlign='center'>
            {productContent[isOnboarding ? 'onboarding' : 'upcoming'].title}
          </PGTypography>
          <PGTypography variant='body' className={classes.subtitle}>
            {productContent[isOnboarding ? 'onboarding' : 'upcoming'].subtitle}
          </PGTypography>
          <Box className={classes.meta}>
            <Box className={classes.metaItem}>
              <AccessTimeIcon sx={{fontSize: '15px'}} />
              <PGTypography variant='caption'>
                {t('consultations_no_consultation_view_duration')}
              </PGTypography>
            </Box>
            <Box className={classes.metaDivider} />
            <Box className={classes.metaItem}>
              <VideocamOutlinedIcon sx={{fontSize: '15px'}} />
              <PGTypography variant='caption'>
                {t('consultations_no_consultation_view_platform')}
              </PGTypography>
            </Box>
            <Box className={classes.metaDivider} />
            <Box className={classes.metaItem}>
              <LockOutlinedIcon sx={{fontSize: '15px'}} />
              <PGTypography variant='caption'>
                {t('consultations_no_consultation_view_confidential')}
              </PGTypography>
            </Box>
          </Box>
        </Box>
        <PGButton
          variant='contained'
          label={t('consultations_no_consultation_view_book_consultation')}
          startIcon={<CallIcon sx={{fontSize: '20px'}} />}
          size='medium'
          onClick={
            onBookClick ??
            (() => history.push(getPath(RouteEnum.CONSULTATIONS)))
          }
        />
      </Box>
    </ConsultationCard>
  )
}

export default NoConsultationView
